import { DateTime } from 'luxon';

import DatadogApi from '@/common/apis/DatadogApi';
import * as globals from '@/globals';
import { BaseApiUrl } from '@/org-app/api';
import { getDiff } from '@/utils/Diff';
import LocalCache from '@/utils/LocalCache';
import Validatable, { IEntityChange } from '@/validator/Validatable';

import { getEntityProperties } from './ReportDefinition.entityProperties';
import ReportDefinitionOptions, { ReportDefinitionOptionsFilterValues } from './ReportDefinitionOptions';
import ReportSchedule, { IReportScheduleAPI } from './ReportSchedule';

export interface IReportDefinitionAPI {
  report_definition_id: number;
  name: string;
  definition_json: IReportDefinitionJSON;
  last_executed_on_utc: string;
  created_on_utc: string;
  modified_on_utc: string;
  emails: string[];
  report_schedules: IReportScheduleAPI[];
  default_report_output_format: string;
  preferred_runner_type: string;
  created_by: string;
}

export interface IReportDefinitionJSON {
  dimensions: string[];
  metrics: string[];
  breakdown: string;
  format: string;
  dates: {
    mode: string;
    date_range: string;
    start_date: string;
    end_date: string;
  };
  defaultReportOutputFormat: string;
  timezone: string;
  filters: { property: string; values: number[] }[];
}

interface IReportDefinition {
  id: number;
  name: string;
  definition: IReportDefinitionJSON;
  lastExecutedOnUtc: string;
  createdOnUtc: string;
  modifiedOnUtc: string;
  emailTargets: string[];
  reportSchedules: ReportSchedule[];
  defaultReportOutputFormat: string;
  preferredRunnerType: string;
  createdBy: string;
}

export default class ReportDefinition extends Validatable implements IReportDefinition {
  apiObject: IReportDefinitionAPI;
  cachedOriginalObject: ReportDefinition;

  name: string;
  definition: IReportDefinitionJSON;
  timestamp: DateTime;
  lastExecutedOnUtc: string;
  createdOnUtc: string;
  modifiedOnUtc: string;
  emailTargets: string[];
  reportSchedules: ReportSchedule[];
  defaultReportOutputFormat: string;
  preferredRunnerType: string;
  createdBy: string;

  constructor(
    id = -1,
    name = '',
    definition = null,
    lastExecutedOnUtc = null,
    createdOnUtc = null,
    modifiedOnUtc = null,
    emailTargets = [],
    reportSchedules = null,
    defaultReportOutputFormat = null,
    preferredRunnerType = null,
    createdBy = null,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.definition = definition;
    this.timestamp = DateTime.local();
    this.lastExecutedOnUtc = lastExecutedOnUtc;
    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.emailTargets = emailTargets;
    this.reportSchedules = reportSchedules;
    this.defaultReportOutputFormat = defaultReportOutputFormat;
    this.preferredRunnerType = preferredRunnerType;
    this.createdBy = createdBy;
  }

  get breakdown() {
    return this.definition?.breakdown;
  }

  get dimensions() {
    return this.definition == null || this.definition.dimensions == null ? [] : this.definition.dimensions;
  }

  set dimensions(val) {
    this.definition.dimensions = val;
  }

  get metrics() {
    return this.definition == null || this.definition.metrics == null ? [] : this.definition.metrics;
  }

  get datesMode() {
    return this.definition.dates.mode;
  }

  get datesRange() {
    return this.definition.dates.date_range;
  }

  get customDateRange() {
    return this.definition.dates.start_date != null && this.definition.dates.end_date != null
      ? `${DateTime.fromISO(this.definition.dates.start_date).toLocaleString(DateTime.DATE_MED)} - ${DateTime.fromISO(this.definition.dates.end_date).toLocaleString(DateTime.DATE_MED)}`
      : globals.DASH;
  }

  get usesCustomDateRange() {
    return this.datesMode === 'ABSOLUTE';
  }

  get endpoint() {
    return `/reportdefinitions/${this.id}`;
  }

  get runReportURL() {
    const organizationId = LocalCache.get('organization').organization_id;
    const jwtToken = `bearer ${LocalCache.get('token')}`;
    return `${BaseApiUrl}reports/fromdefinition/${this.id}?ax-organization-id=${organizationId}&jwt_token=${jwtToken}`;
  }

  get lastExecutedOnFormatted() {
    return this.lastExecutedOnUtc !== null
      ? DateTime.fromISO(this.lastExecutedOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_SHORT)
      : globals.DASH;
  }

  get createdOnFormatted() {
    return this.createdOnUtc != null
      ? DateTime.fromISO(this.createdOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : globals.DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUtc != null
      ? DateTime.fromISO(this.modifiedOnUtc, { zone: 'utc' }).toLocal().toLocaleString(DateTime.DATETIME_MED)
      : globals.DASH;
  }

  get outputFormat() {
    return this.definition.format;
  }

  get reportSchedulesDescription() {
    return this.reportSchedules.map((x) => x.description).join(', ');
  }

  get reportSchedulesTimezone() {
    return this.reportSchedules.map((x) => x.executionTimezone).join(', ');
  }

  get filteredDimensions() {
    if (this.definition != null && this.definition.dimensions?.length > 0) {
      return this.definition.dimensions.filter((x) => x !== 'CADENCE');
    }
    return [];
  }

  get filters() {
    return this.definition.filters;
  }

  get filterOthers() {
    const knownKeys = ReportDefinitionOptions.allFilterOptions().map((x) => x.value);
    const otherFilters = this.filters.filter((x) => !knownKeys.includes(x.property));
    return otherFilters.map((x) => `${x.property}: ${x.values.join(', ')}`).join('; ');
  }

  get filterAccounts() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Account)?.values || [];
  }

  get filterAdvertisers() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Advertiser)?.values || [];
  }

  get filterBuyers() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Buyer)?.values || [];
  }

  get filterBuyingPlatforms() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.BuyingPlatform)?.values || [];
  }

  get filterCampaigns() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Campaign)?.values || [];
  }

  get filterCities() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.City)?.values || [];
  }

  get filterCountries() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Country)?.values || [];
  }

  get filterCreatives() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Creative)?.values || [];
  }

  get filterCreativeFiles() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.CreativeFile)?.values || [];
  }

  get filterCreativeFileHeights() {
    return (
      this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.CreativeFileHeight)?.values || []
    );
  }

  get filterCreativeFileWidths() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.CreativeFileWidth)?.values || [];
  }

  get filterCuratedDeals() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.CuratedDeal)?.values || [];
  }

  get filterCuratedDealIDs() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.CuratedDealID)?.values || [];
  }

  get filterDeals() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Deal)?.values || [];
  }

  get filterDealIDs() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.DealID)?.values || [];
  }

  get filterLineItems() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.LineItem)?.values || [];
  }

  get filterMarkets() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Market)?.values || [];
  }

  get filterMediaOwners() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.MediaOwner)?.values || [];
  }

  get filterMediaTypes() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.MediaType)?.values || [];
  }

  get filterMediaTypeChildren() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.MediaTypeChild)?.values || [];
  }

  get filterMediaTypeGrandchildren() {
    return (
      this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.MediaTypeGrandchild)?.values || []
    );
  }

  get filterMediaTypeParents() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.MediaTypeParent)?.values || [];
  }

  get filterNetworks() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Network)?.values || [];
  }

  get filterRegions() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Region)?.values || [];
  }

  get filterRevenueSources() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.RevenueSource)?.values || [];
  }

  get filterSites() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Site)?.values || [];
  }

  get filterUnits() {
    return this.filters.find((x) => x.property === ReportDefinitionOptionsFilterValues.Unit)?.values || [];
  }

  isPristine(): boolean {
    if (this.isNew) return true;

    const ignoreProperties = [
      'created_on_utc',
      'modified_on_utc',
      'created_by',
      'modified_by',
      'is_admin_report',
      'exchange_id',
      'organization_id',
      'report_schedules',
      'last_executed_on_utc',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);

    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange([...diffs]);
    return false;
  }

  get originalObject(): ReportDefinition {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = ReportDefinition.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  async getChanges(): Promise<IEntityChange<ReportDefinition>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<ReportDefinition>(this.originalObject, ReportDefinition.entityProperties);
  }

  static fromApi(apiObject: IReportDefinitionAPI) {
    const reportDefinition = new ReportDefinition(
      apiObject.report_definition_id,
      apiObject.name,
      apiObject.definition_json,
      apiObject.last_executed_on_utc !== null && apiObject.last_executed_on_utc !== undefined
        ? DateTime.fromISO(apiObject.last_executed_on_utc, { zone: 'utc' })
        : null,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.emails,
      apiObject.report_schedules != null ? apiObject.report_schedules.map((x) => ReportSchedule.fromApi(x)) : [],
      apiObject.default_report_output_format,
      apiObject.preferred_runner_type,
      apiObject.created_by,
    );
    reportDefinition.apiObject = JSON.parse(JSON.stringify(apiObject)); // deep copy because of definition_json.
    return reportDefinition;
  }

  static fromObject(obj: IReportDefinition) {
    return super.fromObject(obj, new ReportDefinition());
  }

  toApi() {
    return {
      report_definition_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      definition_json: this.definition,
      last_executed_on_utc: globals.toServerDate(this.lastExecutedOnUtc),
      emails: this.emailTargets,
      default_report_output_format: this.defaultReportOutputFormat,
      preferred_runner_type: this.preferredRunnerType,
    };
  }

  static validatorCreateRules() {
    return {
      name: 'required|max:200|not_empty_or_whitespace',
      'definition.metrics': {
        rule: 'not_empty',
        formName: 'metrics',
      },
    };
  }

  static get entityProperties() {
    if (ReportDefinition.entityPropertiesCached != null) {
      return ReportDefinition.entityPropertiesCached;
    }
    ReportDefinition.entityPropertiesCached = getEntityProperties();
    return ReportDefinition.entityPropertiesCached;
  }
}
